import { useEffect, useRef, useState } from "react";
import BorderBox from "./BorderBox.jsx";
import Spinner from "./Spinner.jsx";
import GrandMasterMessageModal from "./GrandMasterMessageModal.jsx";
import requestHandler from "../utils/requestHandler.js";
import playButton from "../assets/images/btn_grand-master-video-play.png";

export default function GrandMasterMessage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [scaleIn, setScaleIn] = useState(false);

  const lastFocusedRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const res = await requestHandler({
        path: "grand-master-message",
        method: "GET",
      });

      console.log("[GrandMasterMessage] response:", res);

      if (cancelled) return;

      if (res.success) {
        setData(res.data);
      } else {
        setError(res.error || "Failed to load message");
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const openModal = () => {
    lastFocusedRef.current = document.activeElement;
    setIsExpanded(true);
  };

  const closeModal = () => setIsExpanded(false);

  useEffect(() => {
    if (!isExpanded) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isExpanded]);

  useEffect(() => {
    if (isExpanded) {
      requestAnimationFrame(() => setScaleIn(true));
    } else {
      setScaleIn(false);
    }
  }, [isExpanded]);

  useEffect(() => {
    if (isExpanded && modalRef.current) {
      modalRef.current.focus();
    } else if (!isExpanded && lastFocusedRef.current) {
      lastFocusedRef.current.focus();
    }
  }, [isExpanded]);

  return (
    <>
      <BorderBox noPadding>
        <div className="flex flex-col w-full h-full">
          <div className="text-lz-candle-dim dark:text-lz-concrete text-2xl font-semibold text-center uppercase shrink-0 my-4">
            Grand Master&apos;s Message
          </div>

          <div className="flex-1 min-h-0 flex items-start justify-center px-10 mb-4 text-lz-ink-mid dark:text-white">
            {loading && <Spinner />}

            {!loading && error && (
              <div className="text-sm text-red-500 dark:text-red-400 text-center">
                {error}
              </div>
            )}

            {!loading && !error && data && (
              <div className="flex flex-col items-center gap-4 max-h-full min-h-0">
                {data.body && (
                  <p className="text-base leading-relaxed text-center whitespace-pre-wrap shrink-0">
                    {data.body}
                  </p>
                )}

                {data.image && (
                  <button
                    type="button"
                    onClick={openModal}
                    className="relative block aspect-square max-h-full max-w-xs min-h-0 rounded-2xl overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-lz-ember group"
                    aria-label="Open Grand Master's video message"
                  >
                    <img
                      src={data.image}
                      alt="Grand Master's message"
                      className="block w-full h-full object-cover rounded-2xl transition-opacity group-hover:opacity-90"
                    />
                    <img
                      src={playButton}
                      alt=""
                      aria-hidden="true"
                      className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1/4 max-w-[96px] transition-transform group-hover:scale-105"
                    />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </BorderBox>

      {isExpanded && (
        <GrandMasterMessageModal
          videoIframe={data?.video_iframe}
          scaleIn={scaleIn}
          onClose={closeModal}
          modalRef={modalRef}
        />
      )}
    </>
  );
}
